import { log } from 'core/utils/logger';
import EventEmitter from 'eventemitter3';

/**
 * @typedef {object} MessageBus
 * @desc A custom pub-sub application for general use case based on events.
 * @property {function(eventName: string), payload: object} trigger - Trigger an event with payload
 * @property {function(eventName: string), methodToCall, thisBinding} on - Subscribe to an event
 * @property {function(eventName: string), methodToCall, thisBinding} on - Unsubscribe from an event
 * @example
 * import { messageBus } from 'core/modules/message-bus';
 *
 * const unsubscribe = messageBus.on( 'SOME_EVENT', subscriber, context );
 * messageBus.trigger( 'SOME_EVENT', { key: 'val' } );
 *
 * // unsubscribe from an event
 * unsubscribe(); // or => messageBus.off( 'SOME_EVENT', subscriber, context );
 */
export class MessageBus {
    constructor( { prefix = undefined } = {} ) {

        // create logger with prefix
        this.logger = prefix ? log.tag( `MESSAGE_BUS:${ prefix }` ) : log.tag( 'MESSAGE_BUS' );

        // event emitter
        this.emitter = new EventEmitter();
    }

    /**
     * @desc Add a listener for the specified event.
     * @param {*} event - name of the event
     * @param {*} listener - a function to execute when the event is triggered
     * @param {*} context - execution context of the function
     */
    on( event, listener, context ) {
        this.logger.debug( `A listener added for the ${ event } event.` );

        // subscribe
        this.emitter.on( event, listener, context );

        // return an unsubscribe function
        return () => {
            this.off( event, listener, context );
        };
    }

    /**
     * @desc Add a one-time listener for the specified event.
     * @param {*} event - name of the event
     * @param {*} listener - a function to execute when the event is triggered
     * @param {*} context - execution context of the function
     */
    once( event, listener, context ) {
        this.logger.debug( `A listener added for the ${ event } event.` );

        // subscribe
        this.emitter.once( event, listener, context );

        // return an unsubscribe function
        return () => {
            this.off( event, listener, context );
        };
    }

    /**
     * @desc Remove a listener of the specified event.
     * @param {*} event - name of the event
     * @param {*} listener - a function to execute when the event is triggered
     * @param {*} context - execution context of the function
     */
    off( event, listener, context ) {
        this.logger.debug( `A listener was removed of the ${ event } event.` );

        // unsubscribe
        this.emitter.off( event, listener, context );
    }

    /**
     * @desc Remove all listeners of the specified event.
     * @param {*} event - name of the event
     */
    offAll() {
        this.logger.debug( `All listeners were removed of the ${ event } event.` );

        // unsubscribe
        this.emitter.off( event );
    }

    /**
     * @desc Trigger (emit) an event with specified payload.
     * @param {*} event - name of the event
     * @param {*} payload - data to broadcast to all event listeners
     */
    trigger( event, payload ) {
        this.logger.debug( `Triggered ${ event } event.` );

        // emit
        this.emitter.emit( event, payload );
    }
}


/**
 * @desc A factory function to create `MessageBus` instance.
 * @param {*} prefix - a prefix to appear with log messages
 */
export const messageBusFactory = ( { prefix } = {} ) => {
    return new MessageBus( { prefix } );
};


/**
 * @desc global `MessageBus` instance
 */
export const messageBus = messageBusFactory();

/**
 * @desc Get an event emitter which only triggers and listens to one event.
 * @example
 * import { getEmitter } from 'core/modules/message-bus';
 *
 * const messageBus = getEmitter();
 *
 * messageBus.trigger();
 * messageBus.on( callback, thisObject );
 * messageBus.off( callback, thisObject );
 *
 * @returns {object} - an instance of MessageBus
 */
export const getEmitter = ( eventName ) => {
    return {
        on: ( ...params ) => {
            return messageBus.on( eventName, ...params );
        },
        off: ( ...params ) => {
            return messageBus.off( eventName, ...params );
        },
        trigger: ( ...params ) => {
            return messageBus.trigger( eventName, ...params );
        }
    };
};
